import React, { useEffect, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Skeleton } from "antd";
import moment from "moment";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { useHistory, useParams } from "react-router-dom";
import {
  ActionBodyText,
  BodyContainer,
  Container,
} from "../../../styles/ContainerStyles";
import HeaderComponent from "../../../components/Header/Header";
import { tableBody, tableHeader, tableName } from "../../../styles/TableStyles";
import CustomInputField from "../../../components/widgets/Inputs/CustomInputField";
import Colors from "../../../config/Colors";
import { useReviewCategory } from "../../../hooks/useCrimes";
import { Calendar, CalendarChangeEvent } from "primereact/calendar";
import { CustomIcons } from "../../../assets";
import BackButton from "../../../components/widgets/common/BackButton";

type categoryParams = {
  category: string;
};

const ReviewCategory = () => {
  const { category } = useParams<categoryParams>();
  const navigate = useHistory();
  const { data, isLoading } = useReviewCategory(category);
  const navigateTo = (id: string) => {
    navigate.push(`/home/crime-report-detail/${id}`);
  };

  const [crime, setCrime] = useState<ITabItemCategory[]>();
  const customInputTheme = {
    token: {
      colorPrimaryHover: `${Colors.appMainColor}`,
    },
  };

  const [filters, setFilters] = useState<DataTableFilterMeta | undefined>(
    undefined
  );

  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      lastUpdated: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
      },
      reportedBy: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
      },
      crimeType: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
      },
      category: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
      },
      location: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
      },
      dateReported: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
      },
      caseNo: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
      },
      carInsurance: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      carRegistration: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      vehicleMake: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      vehicleType: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
    });
    setGlobalFilterValue("");
  };

  const clearFilter = () => {
    initFilters();
  };

  const onGlobalFilterChangeV2 = (e: string) => {
    const _filters = { ...filters };
    // @ts-ignore
    _filters.global.value = e;

    setFilters(_filters);
    setGlobalFilterValue(e);
  };
  const getCrimes = (dataTabs: ITabItemCategory[]) => {
    return [...(dataTabs || [])].map((d) => {
      // @ts-ignore
      d.dateReported = new Date(d.dateReported);

      return d;
    });
  };

  useEffect(() => {
    if (Array.isArray(data)) {
      setCrime(getCrimes(data));
    }
    setGlobalFilterValue("");

    initFilters();
  }, [data]);

  const dateBodyTemplate = (rowData: ITabItemCategory) => {
    return moment(rowData.lastUpdated).format("YYYY/MM/DD");
  };
  const categoryBodyTemplate = (rowData: ITabItemCategory) => {
    return rowData.category;
  };
  const reportedbyBodyTemplate = (rowData: ITabItemCategory) => {
    return rowData.reportedBy;
  };
  const locationBodyTemplate = (rowData: ITabItemCategory) => {
    return rowData.location;
  };
  const timeOfCrimeBodyTemplate = (rowData: ITabItemCategory) => {
    return rowData.time;
  };
  const dateReportedBodyTemplate = (rowData: ITabItemCategory) => {
    return moment(rowData.dateReported).format("YYYY/MM/DD");
  };
  const caseNoBodyTemplate = (rowData: ITabItemCategory) => {
    return rowData.caseNo;
  };
  const crimeTypeBodyTemplate = (rowData: ITabItemCategory) => {
    return rowData.crimeType;
  };

  const carInsuranceBodyTemplate = (rowData: ITabItemCategory) => {
    return rowData.carInsurance;
  };
  const carRegistrationBodyTemplate = (rowData: ITabItemCategory) => {
    return rowData.carRegistration;
  };
  const vehicleMakeBodyTemplate = (rowData: ITabItemCategory) => {
    return rowData.vehicleMake;
  };
  const vehicleTypeBodyTemplate = (rowData: ITabItemCategory) => {
    return rowData.vehicleType;
  };

  const viewMoreBodyTemplate = (rowData: ITabItemCategory) => {
    const id = rowData.crimeId;
    return (
      <ActionBodyText
        onClick={() => {
          navigateTo(id);
        }}
        className="cursor-pointer"
      >
        View More
      </ActionBodyText>
    );
  };

  const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <Calendar
        value={options.value}
        onChange={(e: CalendarChangeEvent) => {
          options.filterCallback(e.value, options.index);
        }}
        dateFormat="yy/mm/dd"
        placeholder="yyyy/mm/dd"
        mask="9999/99/99"
      />
    );
  };

  // Skeleton component for wide table rows with 12+ columns
  const WideTableRowSkeleton = () => (
    <div
      className="flex items-center gap-2 p-4 border-b border-gray-100"
      style={{ minWidth: "200rem" }}
    >
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "12rem", height: 20 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "12rem", height: 20 }} active />
      </div>
      <div style={{ minWidth: "25rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "20rem", height: 20 }} active />
      </div>
      <div style={{ minWidth: "25rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "20rem", height: 20 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "12rem", height: 20 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "12rem", height: 20 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "12rem", height: 20 }} active />
      </div>
      <div style={{ minWidth: "10rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "8rem", height: 20 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "12rem", height: 20 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "12rem", height: 20 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "12rem", height: 20 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "12rem", height: 20 }} active />
      </div>
      <div style={{ minWidth: "10rem" }}>
        <Skeleton.Button style={{ width: "8rem", height: 20 }} active />
      </div>
    </div>
  );

  // Table header skeleton for wide table
  const WideTableHeaderSkeleton = () => (
    <div
      className="flex items-center gap-2 p-4 bg-gray-50 border-b border-gray-200"
      style={{ minWidth: "200rem" }}
    >
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "10rem", height: 16 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "10rem", height: 16 }} active />
      </div>
      <div style={{ minWidth: "25rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "18rem", height: 16 }} active />
      </div>
      <div style={{ minWidth: "25rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "10rem", height: 16 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "8rem", height: 16 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "10rem", height: 16 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "10rem", height: 16 }} active />
      </div>
      <div style={{ minWidth: "10rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "8rem", height: 16 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "10rem", height: 16 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "12rem", height: 16 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "10rem", height: 16 }} active />
      </div>
      <div style={{ minWidth: "15rem" }} className="pr-2">
        <Skeleton.Input style={{ width: "10rem", height: 16 }} active />
      </div>
      <div style={{ minWidth: "10rem" }}>
        <Skeleton.Input style={{ width: "4rem", height: 16 }} active />
      </div>
    </div>
  );

  // Complete wide table skeleton
  const WideTableSkeleton = () => (
    <div className="w-full border border-gray-200 rounded-lg overflow-hidden overflow-x-auto">
      <WideTableHeaderSkeleton />
      {Array.from({ length: 10 }).map((_, index) => (
        <WideTableRowSkeleton key={`wide-skeleton-row-${index}`} />
      ))}
      {/* Pagination skeleton */}
      <div className="flex items-center justify-between p-4 bg-gray-50 border-t border-gray-200">
        <div className="flex gap-2">
          <Skeleton.Button style={{ width: 30, height: 30 }} active />
          <Skeleton.Button style={{ width: 30, height: 30 }} active />
          <Skeleton.Button style={{ width: 30, height: 30 }} active />
          <Skeleton.Button style={{ width: 30, height: 30 }} active />
        </div>
        <Skeleton.Input style={{ width: 200, height: 20 }} active />
        <Skeleton.Input style={{ width: 100, height: 32 }} active />
      </div>
    </div>
  );

  return (
    <div className="w-full h-full">
      <Container className="h-full w-full">
        <BodyContainer className="h-full w-full">
          <div className="flex flex-row w-full justify-start align-items-center">
            <BackButton />
            <HeaderComponent title="Reported Crimes" />
          </div>
          <div className="px-3 mt-3">
            <div className="flex flex-row gap-1 justify-between mb-2 w-full">
              <div
                className="flex flex-row align-items-center"
                style={tableName}
              >
                {category}
              </div>
              <CustomInputField
                value={globalFilterValue}
                onChange={(e: string) => onGlobalFilterChangeV2(e)}
                onPressEnter={() => {}}
                theme={customInputTheme}
                inputProps={{
                  size: "large",
                  placeholder: "Search",
                  allowClear: true,
                  className: "ml-2 w-3",
                  prefix: <SearchOutlined />,
                }}
                mergeThemes
              />
            </div>

            {isLoading ? (
              <WideTableSkeleton />
            ) : (
              <DataTable
                value={Array.isArray(crime) ? crime : []}
                paginator
                rows={10}
                filters={filters}
                globalFilterFields={[
                  "lastUpdated",
                  "category",
                  "reportedBy",
                  "crimeType",
                  "location",
                  "dateReported",
                  "caseNo",
                  "carInsurance",
                  "carRegistration",
                  "vehicleMake",
                  "vehicleType",
                ]}
                rowsPerPageOptions={[10, 25, 50, 100]}
                tableStyle={{ minWidth: "50rem" }}
                sortMode="multiple"
                emptyMessage="No reported crimes found."
                scrollable
                scrollHeight="40rem"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Operators"
                stateStorage="session"
                stateKey={`dt-state-crime-review-table`}
              >
                <Column
                  field="lastUpdated"
                  header="Last Updated"
                  dataType="date"
                  filterField="lastupdated"
                  headerStyle={tableHeader}
                  style={{ background: "#FBFBFB", minWidth: "15rem" }}
                  bodyStyle={tableBody}
                  body={dateBodyTemplate}
                  filter
                  filterElement={dateFilterTemplate}
                />
                <Column
                  field="reportedBy"
                  header="Reported By"
                  headerStyle={tableHeader}
                  style={{ background: "#FBFBFB", minWidth: "15rem" }}
                  bodyStyle={tableBody}
                  body={reportedbyBodyTemplate}
                  filter
                />
                <Column
                  field="category"
                  header="Category of Crime"
                  headerStyle={tableHeader}
                  style={{ background: "#FBFBFB", minWidth: "25rem" }}
                  bodyStyle={tableBody}
                  body={categoryBodyTemplate}
                  filter
                />
                <Column
                  field="crimeType"
                  header="Crime Type"
                  headerStyle={tableHeader}
                  style={{ background: "#FBFBFB", minWidth: "25rem" }}
                  bodyStyle={tableBody}
                  body={crimeTypeBodyTemplate}
                  filter
                />
                <Column
                  field="location"
                  header="Location"
                  headerStyle={tableHeader}
                  style={{ background: "#FBFBFB", minWidth: "15rem" }}
                  bodyStyle={tableBody}
                  body={locationBodyTemplate}
                  filter
                />
                <Column
                  field="caseNo"
                  header="Case Number"
                  headerStyle={tableHeader}
                  style={{ background: "#FBFBFB", minWidth: "15rem" }}
                  bodyStyle={tableBody}
                  body={caseNoBodyTemplate}
                  filter
                />
                <Column
                  field="dateReported"
                  header="Date Reported"
                  headerStyle={tableHeader}
                  filterField="dateReported"
                  dataType="date"
                  body={dateReportedBodyTemplate}
                  bodyStyle={tableBody}
                  filter
                  filterElement={dateFilterTemplate}
                  style={{ background: "#FBFBFB", minWidth: "15rem" }}
                />
                <Column
                  field="time"
                  header="Time of Crime"
                  headerStyle={tableHeader}
                  style={{ background: "#FBFBFB", minWidth: "10rem" }}
                  bodyStyle={tableBody}
                  body={timeOfCrimeBodyTemplate}
                />
                <Column
                  field="carInsurance"
                  header="Car Insurance"
                  headerStyle={tableHeader}
                  style={{ background: "#FBFBFB", minWidth: "15rem" }}
                  bodyStyle={tableBody}
                  body={carInsuranceBodyTemplate}
                  filter
                />
                <Column
                  field="carRegistration"
                  header="Car Registration"
                  headerStyle={tableHeader}
                  style={{ background: "#FBFBFB", minWidth: "15rem" }}
                  bodyStyle={tableBody}
                  body={carRegistrationBodyTemplate}
                  filter
                />
                <Column
                  field="vehicleMake"
                  header="Vehicle Make"
                  headerStyle={tableHeader}
                  style={{ background: "#FBFBFB", minWidth: "15rem" }}
                  bodyStyle={tableBody}
                  body={vehicleMakeBodyTemplate}
                  filter
                />
                <Column
                  field="vehicleType"
                  header="Vehicle Type"
                  headerStyle={tableHeader}
                  style={{ background: "#FBFBFB", minWidth: "15rem" }}
                  bodyStyle={tableBody}
                  body={vehicleTypeBodyTemplate}
                  filter
                />
                <Column
                  headerStyle={tableHeader}
                  style={{ background: "#FBFBFB", minWidth: "10rem" }}
                  bodyStyle={tableBody}
                  body={viewMoreBodyTemplate}
                />
              </DataTable>
            )}
          </div>
        </BodyContainer>
      </Container>
    </div>
  );
};

export default ReviewCategory;
